import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Overlay, Popover} from 'react-bootstrap'
import {Pipette} from 'lucide-react'

export type HsvaColor = {h: number; s: number; v: number; a: number}

type Props = {
  value: string
  onChange: (hex: string) => void
  disabled?: boolean
  placement?: 'top' | 'bottom' | 'top-start' | 'bottom-start'
}

const DEFAULT_SWATCHES = [
  '#F8371A',
  '#F97C1B',
  '#FAC81C',
  '#3FD0B6',
  '#2CADF6',
  '#6462FC',
  '#FCA5A5',
  '#FDBA74',
  '#FDE68A',
  '#99F6E4',
  '#93C5FD',
  '#C4B5FD',
]

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n))
}

function normalizeHex(raw: string): string {
  let v = (raw || '').trim()
  if (!v.startsWith('#')) v = `#${v}`
  if (/^#[0-9A-Fa-f]{3}$/.test(v)) {
    v = `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`
  }
  if (!/^#[0-9A-Fa-f]{6}$/.test(v)) return '#6B7280'
  return v.toUpperCase()
}

function hexToRgb(hex: string): {r: number; g: number; b: number} {
  const c = normalizeHex(hex).slice(1)
  return {
    r: parseInt(c.slice(0, 2), 16),
    g: parseInt(c.slice(2, 4), 16),
    b: parseInt(c.slice(4, 6), 16),
  }
}

function rgbToHex(r: number, g: number, b: number): string {
  const to = (n: number) =>
    clamp(Math.round(n), 0, 255)
      .toString(16)
      .padStart(2, '0')
      .toUpperCase()
  return `#${to(r)}${to(g)}${to(b)}`
}

function rgbToHsva(r: number, g: number, b: number): HsvaColor {
  r /= 255
  g /= 255
  b /= 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const d = max - min
  let h = 0
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h *= 60
    if (h < 0) h += 360
  }
  const s = max === 0 ? 0 : (d / max) * 100
  const v = max * 100
  return {h, s, v, a: 1}
}

function hsvaToRgb(hsva: HsvaColor): {r: number; g: number; b: number} {
  const h = ((hsva.h % 360) + 360) % 360
  const s = clamp(hsva.s, 0, 100) / 100
  const v = clamp(hsva.v, 0, 100) / 100
  const c = v * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = v - c
  let rp = 0
  let gp = 0
  let bp = 0
  if (h < 60) [rp, gp, bp] = [c, x, 0]
  else if (h < 120) [rp, gp, bp] = [x, c, 0]
  else if (h < 180) [rp, gp, bp] = [0, c, x]
  else if (h < 240) [rp, gp, bp] = [0, x, c]
  else if (h < 300) [rp, gp, bp] = [x, 0, c]
  else [rp, gp, bp] = [c, 0, x]
  return {
    r: (rp + m) * 255,
    g: (gp + m) * 255,
    b: (bp + m) * 255,
  }
}

function hsvaToHex(hsva: HsvaColor): string {
  const {r, g, b} = hsvaToRgb(hsva)
  return rgbToHex(r, g, b)
}

function hexToHsva(hex: string): HsvaColor {
  const {r, g, b} = hexToRgb(hex)
  return rgbToHsva(r, g, b)
}

function relativeLuminance(hex: string): number {
  const {r, g, b} = hexToRgb(hex)
  const toLin = (c: number) => {
    const ch = c / 255
    return ch <= 0.03928 ? ch / 12.92 : Math.pow((ch + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b)
}

export function contrastTextForHex(hex: string): '#111111' | '#FFFFFF' {
  return relativeLuminance(hex) > 0.45 ? '#111111' : '#FFFFFF'
}

function hueCss(h: number): string {
  return `hsl(${h}, 100%, 50%)`
}

const PICKER_STYLE_ID = 'call-tag-picker-styles'
if (typeof document !== 'undefined' && !document.getElementById(PICKER_STYLE_ID)) {
  const style = document.createElement('style')
  style.id = PICKER_STYLE_ID
  style.textContent = `
    .call-tag-color-picker { z-index: 2200 !important; }
    .call-tags-more-picker { z-index: 2100 !important; }
  `
  document.head.appendChild(style)
}

const CallTagColorPicker: React.FC<Props> = ({
  value,
  onChange,
  disabled,
  placement = 'top',
}) => {
  const [open, setOpen] = useState(false)
  const [hsva, setHsva] = useState<HsvaColor>(() => hexToHsva(value || '#6B7280'))
  const [hexDraft, setHexDraft] = useState(() => normalizeHex(value || '#6B7280'))
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const satRef = useRef<HTMLDivElement | null>(null)
  const hueRef = useRef<HTMLDivElement | null>(null)
  const dragging = useRef<'sat' | 'hue' | null>(null)
  const hsvaRef = useRef(hsva)
  hsvaRef.current = hsva
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const dragCleanupRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    if (dragging.current) return
    const next = normalizeHex(value || '#6B7280')
    setHsva(hexToHsva(next))
    setHexDraft(next)
  }, [value])

  const hex = useMemo(() => hsvaToHex(hsva), [hsva])
  const contrastOnWhite = useMemo(() => {
    const L = relativeLuminance(hex)
    return Number(((L + 0.05) / 0.05).toFixed(2))
  }, [hex])
  const previewFg = contrastTextForHex(hex)

  const paint = useCallback((next: HsvaColor, notifyParent: boolean) => {
    hsvaRef.current = next
    setHsva(next)
    const h = hsvaToHex(next)
    setHexDraft(h)
    if (notifyParent) onChangeRef.current(h)
  }, [])

  const commit = useCallback(
    (next: HsvaColor) => {
      paint(next, true)
    },
    [paint]
  )

  const applyPointer = useCallback(
    (clientX: number, clientY: number, notifyParent: boolean) => {
      const kind = dragging.current
      if (!kind) return
      if (kind === 'sat') {
        const el = satRef.current
        if (!el) return
        const rect = el.getBoundingClientRect()
        const x = clamp((clientX - rect.left) / rect.width, 0, 1)
        const y = clamp((clientY - rect.top) / rect.height, 0, 1)
        paint({...hsvaRef.current, s: x * 100, v: (1 - y) * 100}, notifyParent)
        return
      }
      const el = hueRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const x = clamp((clientX - rect.left) / rect.width, 0, 1)
      paint({...hsvaRef.current, h: x * 360}, notifyParent)
    },
    [paint]
  )

  const stopDrag = useCallback(
    (clientX: number, clientY: number) => {
      applyPointer(clientX, clientY, true)
      dragging.current = null
      dragCleanupRef.current?.()
      dragCleanupRef.current = null
    },
    [applyPointer]
  )

  const startDrag = useCallback(
    (kind: 'sat' | 'hue', event: React.MouseEvent) => {
      event.preventDefault()
      dragCleanupRef.current?.()
      dragging.current = kind
      applyPointer(event.clientX, event.clientY, false)
      const onMove = (e: MouseEvent) => applyPointer(e.clientX, e.clientY, false)
      const onUp = (e: MouseEvent) => stopDrag(e.clientX, e.clientY)
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
      dragCleanupRef.current = () => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
    },
    [applyPointer, stopDrag]
  )

  useEffect(() => {
    return () => {
      dragging.current = null
      dragCleanupRef.current?.()
      dragCleanupRef.current = null
    }
  }, [])

  const swatches = DEFAULT_SWATCHES

  return (
    <>
      <button
        ref={triggerRef}
        type='button'
        className='btn btn-sm border d-inline-flex align-items-center justify-content-center bg-white'
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        onMouseDown={(e) => e.stopPropagation()}
        title='Pick tag color'
        aria-label='Pick tag color'
        style={{width: 34, height: 31, padding: 0, flexShrink: 0, gap: 3}}
      >
        <span
          style={{
            width: 14,
            height: 14,
            borderRadius: 4,
            backgroundColor: hex,
            border: '1px solid #D8DCE3',
            display: 'inline-block',
            flexShrink: 0,
          }}
        />
        <Pipette size={14} strokeWidth={2} className='text-muted' />
      </button>

      <Overlay
        show={open}
        target={triggerRef.current}
        placement={placement}
        container={document.body}
        rootClose
        onHide={() => setOpen(false)}
      >
        <Popover
          id='call-tag-color-picker'
          className='call-tag-color-picker shadow'
          style={{width: 320, maxWidth: 320, zIndex: 2200}}
        >
          <Popover.Body
            className='p-3'
            onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <div
              ref={satRef}
              role='presentation'
              onMouseDown={(e) => startDrag('sat', e)}
              style={{
                width: '100%',
                aspectRatio: '4 / 2',
                borderRadius: 6,
                border: '1px solid #D8DCE3',
                position: 'relative',
                cursor: 'crosshair',
                backgroundImage: `
                  linear-gradient(to top, #000, transparent),
                  linear-gradient(to right, #fff, ${hueCss(hsva.h)})
                `,
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  left: `${hsva.s}%`,
                  top: `${100 - hsva.v}%`,
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  border: '2px solid #fff',
                  boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none',
                  backgroundColor: hex,
                }}
              />
            </div>

            <div
              ref={hueRef}
              role='presentation'
              className='mt-2'
              onMouseDown={(e) => startDrag('hue', e)}
              style={{
                width: '100%',
                height: 14,
                borderRadius: 6,
                cursor: 'ew-resize',
                position: 'relative',
                background:
                  'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  left: `${(hsva.h / 360) * 100}%`,
                  top: '50%',
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  border: '2px solid #fff',
                  boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none',
                  backgroundColor: hueCss(hsva.h),
                }}
              />
            </div>

            <div className='d-flex align-items-center gap-2 mt-2'>
              <span className='badge bg-light text-dark border text-uppercase fs-8 px-2 py-2'>
                HEX
              </span>
              <input
                type='text'
                className='form-control form-control-sm font-monospace'
                value={hexDraft}
                onChange={(e) => setHexDraft(e.target.value)}
                onBlur={() => {
                  const next = normalizeHex(hexDraft)
                  setHexDraft(next)
                  commit(hexToHsva(next))
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    const next = normalizeHex(hexDraft)
                    setHexDraft(next)
                    commit(hexToHsva(next))
                  }
                }}
                maxLength={7}
              />
            </div>

            <hr className='my-2' />

            <div
              className='d-flex flex-nowrap align-items-center justify-content-between'
              style={{width: '100%', gap: 4}}
            >
              {swatches.map((color) => {
                const selected = hex === normalizeHex(color)
                return (
                  <button
                    key={color}
                    type='button'
                    aria-label={`Set color to ${color}`}
                    onClick={() => commit(hexToHsva(color))}
                    style={{
                      width: 16,
                      height: 16,
                      flex: '0 0 16px',
                      borderRadius: 4,
                      backgroundColor: color,
                      border: '1px solid rgba(0,0,0,0.1)',
                      boxShadow: selected ? '0 0 0 1px #fff, 0 0 0 2px #333' : 'none',
                      outline: 'none',
                      padding: 0,
                      cursor: 'pointer',
                    }}
                  />
                )
              })}
            </div>

            <hr className='my-2' />

            <div className='d-flex align-items-center justify-content-between gap-2'>
              <div className='d-flex align-items-center gap-2'>
                <div
                  className='d-flex align-items-center justify-content-center fw-semibold'
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 6,
                    backgroundColor: hex,
                    color: previewFg,
                  }}
                >
                  A
                </div>
                <div className='d-flex flex-column'>
                  <span className='text-muted fs-8'>Contrast Ratio</span>
                  <span className='fs-7 fw-semibold'>{contrastOnWhite}</span>
                </div>
              </div>
              <div className='d-flex gap-1'>
                <span
                  className={`badge rounded-pill ${
                    contrastOnWhite > 4.5 ? 'bg-success bg-opacity-25 text-success' : 'bg-light text-muted border'
                  }`}
                >
                  {contrastOnWhite > 4.5 ? '✓' : '×'} AA
                </span>
                <span
                  className={`badge rounded-pill ${
                    contrastOnWhite > 7 ? 'bg-success bg-opacity-25 text-success' : 'bg-light text-muted border'
                  }`}
                >
                  {contrastOnWhite > 7 ? '✓' : '×'} AAA
                </span>
              </div>
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  )
}

export default CallTagColorPicker
